// Protocolo do Hábito — editar o contrato e o mínimo.
//
// O diagnóstico faz a melhor pergunta do produto — "quinta e sexta passaram em
// branco todas as vezes. Seu gatilho acontece nesses dias?" — e o botão dela
// levava a um cartão somente leitura. O app perguntava e não deixava responder.
//
// As duas rotas já existiam desde o começo (`POST /contrato`, `PATCH /ajustes`);
// o que faltava era tela. Se o seu gatilho é "depois do jantar" e às quintas
// você janta fora, isto aqui é a diferença entre consertar e desistir.
//
// O contrato continua com DUAS variáveis. Editar não é oportunidade de pedir
// mais campos: quando + onde rende 0,46, e acrescentar "como" e "quanto tempo"
// derruba para 0,24.

import React, { useEffect, useState } from 'react';
import { Sheet } from '../common';
import { Icon } from '../icons';
import { AuthSession } from '../../types';
import { EstadoHabito, Sugestoes, habitoApi } from './api';

const serif = 'Instrument Serif, Georgia, serif';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-3)', fontWeight: 600,
};

const Opcao: React.FC<{ texto: string; sel: boolean; onClick: () => void; nota?: string }> = ({ texto, sel, onClick, nota }) => (
  <button onClick={onClick} style={{
    display: 'block', width: '100%', textAlign: 'left', padding: '13px 15px',
    borderRadius: 16, marginBottom: 8, cursor: 'pointer',
    background: sel ? 'var(--ember-soft)' : 'var(--paper-2)',
    border: `1px solid ${sel ? 'var(--ember)' : 'var(--line)'}`,
  }}>
    <div style={{ fontFamily: serif, fontSize: 18, color: 'var(--ink)' }}>{texto}</div>
    {nota && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>{nota}</div>}
  </button>
);

export const EditarContrato: React.FC<{
  aberto: boolean;
  session: AuthSession | null;
  estado: EstadoHabito | null;
  onFechar: () => void;
  onSalvo: () => void;
}> = ({ aberto, session, estado, onFechar, onSalvo }) => {
  const [sug, setSug] = useState<Sugestoes | null>(null);
  const [quando, setQuando] = useState('');
  const [kind, setKind] = useState<'rotina' | 'horario'>('rotina');
  const [hora, setHora] = useState('21:00');
  const [onde, setOnde] = useState('');
  const [minimo, setMinimo] = useState(1);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    habitoApi.sugestoes(session).then(setSug).catch(() => {});
    const c = estado?.contrato;
    setKind(c?.quandoKind || 'rotina');
    setQuando(c?.quando || '');
    setHora(c?.quandoHora?.slice(0, 5) || '21:00');
    setOnde(c?.onde || '');
    setMinimo(estado?.inscricao?.minimoPaginas || 1);
    setErro(null);
  }, [aberto, estado, session]);

  async function salvar() {
    if (!quando.trim() || !onde.trim()) return;
    setSalvando(true); setErro(null);
    try {
      await habitoApi.salvarContrato(session, {
        quando: kind === 'horario' ? `às ${hora}` : quando,
        quandoKind: kind,
        quandoHora: kind === 'horario' ? `${hora}:00` : null,
        onde,
        sugerido: false,
      });
      if (minimo !== (estado?.inscricao?.minimoPaginas || 1)) {
        await habitoApi.ajustes(session, { minimoPaginas: minimo });
      }
      onSalvo();
      onFechar();
    } catch (e: any) {
      setErro(e?.message || 'Não consegui salvar.');
    } finally { setSalvando(false); }
  }

  const gatilhos = sug?.gatilhos || [];
  const lugares = sug?.lugares || [];

  return (
    <Sheet open={aberto} onClose={onFechar} title="Seu plano">
      <div style={{ paddingBottom: 16, maxHeight: '72vh', overflowY: 'auto' }}>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)', margin: '0 0 16px' }}>
          Plano que não cabe na sua semana não é disciplina que falta — é plano errado.
          Trocar aqui não zera nada do seu programa.
        </p>

        <div className="mono" style={{ ...eyebrow, marginBottom: 9 }}>Quando</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 11 }}>
          {([['rotina', 'Depois de algo'], ['horario', 'Num horário']] as const).map(([k, r]) => (
            <button key={k} onClick={() => setKind(k)} style={{
              flex: 1, padding: '9px 12px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
              border: `1px solid ${kind === k ? 'var(--ember)' : 'var(--line)'}`,
              background: kind === k ? 'var(--ember-soft)' : 'transparent',
              color: kind === k ? 'var(--ember)' : 'var(--ink-3)',
            }}>{r}</button>
          ))}
        </div>

        {kind === 'horario' ? (
          <input
            type="time" value={hora} onChange={(e) => setHora(e.target.value)}
            style={{
              width: '100%', padding: '13px 15px', borderRadius: 14, fontSize: 19, marginBottom: 16,
              background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink)',
              fontFamily: 'Geist Mono, monospace',
            }}
          />
        ) : (
          <div style={{ marginBottom: 16 }}>
            {gatilhos.map((g) => (
              <Opcao key={g.id} texto={g.texto.replace(/^assim que eu /, 'Assim que eu ')}
                sel={quando === g.texto} onClick={() => setQuando(g.texto)}
                nota={g.destaque ? 'o tipo de gatilho com maior efeito medido' : undefined} />
            ))}
          </div>
        )}

        <div className="mono" style={{ ...eyebrow, marginBottom: 9 }}>Onde</div>
        <div style={{ marginBottom: 18 }}>
          {lugares.map((l) => (
            <Opcao key={l.id} texto={l.texto} sel={onde === l.texto} onClick={() => setOnde(l.texto)} />
          ))}
        </div>

        {/* O mínimo fica FORA do contrato de propósito — mas continua sendo seu.
            A API sempre aceitou de 1 a 100; o que não existia era o controle. */}
        <div className="mono" style={{ ...eyebrow, marginBottom: 9 }}>O mínimo que vence o dia</div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10,
          padding: '14px 16px', borderRadius: 16, background: 'var(--paper-2)', border: '1px solid var(--line)',
        }}>
          <button onClick={() => setMinimo((v) => Math.max(1, v - 1))} style={{
            width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--line-strong)',
            background: 'transparent', color: 'var(--ink)', fontSize: 20, cursor: 'pointer',
          }}>−</button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontFamily: serif, fontSize: 30, lineHeight: 1, color: 'var(--ink)' }}>{minimo}</div>
            <div style={{ ...eyebrow, fontSize: 9.5, marginTop: 4 }}>{minimo === 1 ? 'página' : 'páginas'}</div>
          </div>
          <button onClick={() => setMinimo((v) => Math.min(100, v + 1))} style={{
            width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--line-strong)',
            background: 'transparent', color: 'var(--ink)', fontSize: 20, cursor: 'pointer',
          }}>+</button>
        </div>
        <p style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-3)', margin: '0 0 18px' }}>
          {minimo === 1
            ? 'Uma página é ridícula de propósito: o objetivo do dia não é ler bastante, é não quebrar a cadeia. Quem abre o livro raramente lê só uma página.'
            : `${minimo} páginas. Você pode subir — mas o mínimo pequeno é o que faz a promessa nunca dar medo, e quem vence o dia é a frequência, não o volume.`}
        </p>

        {erro && <div style={{ fontSize: 13, color: '#E2574C', marginBottom: 12 }}>{erro}</div>}

        <div style={{
          display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 16,
          padding: '12px 14px', borderRadius: 14, background: 'var(--paper)', border: '1px solid var(--line)',
        }}>
          <Icon name="target" size={14} color="var(--ember)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-2)' }}>
            Continuam sendo duas variáveis. Não vou perguntar quantas páginas nem por
            quanto tempo dentro do plano — cada campo a mais derruba o efeito.
          </div>
        </div>

        <button onClick={salvar} disabled={salvando || !quando.trim() || !onde.trim()} style={{
          width: '100%', padding: '16px 20px', borderRadius: 999, border: 'none',
          background: (quando.trim() && onde.trim()) ? 'var(--ember)' : 'var(--paper-3)',
          color: (quando.trim() && onde.trim()) ? '#fff' : 'var(--ink-4)',
          fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
        }}>
          {salvando ? 'um instante…' : 'Salvar o plano'}
        </button>
      </div>
    </Sheet>
  );
};

export default EditarContrato;
